import axios, { AxiosInstance, AxiosResponse, } from 'axios';
import { attachAxiosApiInterceptors } from '../utils/axios-utils';
import { Dispatch } from '@reduxjs/toolkit';
import { ApiPageInfo, MerchantMid, DailyProcessingFilters, TransactionFilters, ReconcileTransactionsFilters } from './api.service-types';
import { UserModel } from '../models';
import { Processor } from '../types/processor.types';
import { StatusType } from '../types/statistics.types';
import { PaymentInformation } from '../models/3ds.model';
import { EditMerchantMid } from '../router/private/merchants/edit-mid/edit-mid.types';
import { CreateBanUser } from '../router/private/ban-list/ban-list-page';

class ApiService {

  private readonly client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      // #TODO change url
      baseURL: 'https://backend.xcharger.services/',
      timeout: 300000,
      headers: {
        'Content-Type': 'application/json',
      }
    });

  }

  attachDispatchToAxiosApi(dispatch: Dispatch<any>) {
    attachAxiosApiInterceptors(this.client, dispatch)
  }


  getUserDetails(userId: string): Promise<AxiosResponse<UserModel>> {
    return this.client.get(`/auth/${userId}`)
  }

  //MULTI-MERCHANT VIEW

  getMyAllowedMerchants(): Promise<AxiosResponse<UserModel[]>> {
    return this.client.get(`/auth/me/allowedMerchants`)
  }

  createMultiMerchantUser(user: Partial<UserModel> & { password?: string }, merchantIds: string[]) {
    return this.client.post(`/auth/createMultiMerchantUser`, { user, merchantIds })
  }

  updateAllowedMerchants(userId: string, merchantIds: string[]) {
    return this.client.put(`/auth/${userId}/allowedMerchants`, merchantIds)
  }


  //PROCESSOR

  createProcessor(processor: Omit<Processor, 'id'>) {
    return this.client.post('/processor/create', processor)
  }

  getAllProcessors() {
    return this.client.get('/processor/getAll');
  }


  //MERCHANT

  createMerchant(merchant: any) {
    return this.client.post('/merchant/create', merchant)
  }

  getMerchantById(merchantId: string) {
    return this.client.get(`/merchant/getById/${merchantId}`)
  }

  updateMerchant(merchant: any) {
    return this.client.put(`/auth/update/${merchant.id}`, merchant)
  }

  getMidsByMerchantId(merchantId: string) {
    return this.client.get(`/mid/getByMerchantId/${merchantId}`)
  }

  getMidLimitById(id: string) {
    return this.client.get(`/mid/getMidLimitById/${id}`)
  }

  getAllMerchants() {
    return this.client.get('/merchant/getAll')
  }

  disableMerchant(id: string) {
    return this.client.put(`merchant/disableMerchant/${id}`)
  }

  getMidsByMerchantAndProcessor({ merchantId, processorId }: { merchantId: string | null, processorId: string | null }) {
    const queryString = this.getQueryParams({ merchantId, processorId });
    return this.client.get(`mid/getMidsEnum${queryString}`)
  }

  createMid(merchantId: string, midRequestBody: MerchantMid) {
    return this.client.post(`/merchant/createMid/${merchantId}`, midRequestBody)
  }

  getMidById(id: string) {
    return this.client.get(`/merchant/getMid/${id}`)
  }

  updateMid(updatedMid: EditMerchantMid) {
    return this.client.put(`/merchant/updateMid`, updatedMid)
  }

  //TRANSACTIONS

  getTransactions(filters?: Partial<TransactionFilters>, pageInfo?: ApiPageInfo) {
    const queryString = this.getQueryParams(pageInfo);
    return this.client.post(`/transaction/user/transactionsListRefactored${queryString}`, filters)
  }

  getTransactionById(id: string) {
    return this.client.get(`/transaction/user/getById/${id}`)
  }

  getTransactionsSummary(filters?: Partial<TransactionFilters>) {
    return this.client.post(`/transaction/user/summary`, filters)
  }

  getTransactionsSummaryToday(merchantId?: string) {
    const queryString = this.getQueryParams({ merchantId });
    return this.client.get(`/transaction/user/summaryToday${queryString}`)
  }

  getRecentTransactions(merchantId?: string) {
    const queryString = this.getQueryParams({ merchantId });
    return this.client.get(`/transaction/user/getRecentTransactions${queryString}`)
  }

  refundTransaction(transactionId: string) {
    return this.client.post(`/payment/refundPayment/${transactionId}`)
  }

  addRefundReason(transactionId: string, refundReason: string) {
    return this.client.put(`/transaction/addRefundReason/${transactionId}`, { refundReason: refundReason })
  }

  updateTransactionStatus(transactionId: string, newStatus: StatusType) {
    return this.client.put(`/transaction/admin/updateStatus/${transactionId}/${newStatus}`)
  }

  updateTransactionStatusWithRefundId(transactionId: string, newStatus: StatusType, refundId: string) {
    return this.client.put(`/payment/updatePayment/${transactionId}/${newStatus}/${refundId}`)
  }

  refreshTransaction(transactionId: string) {
    return this.client.put(`/payment/updatePayment/${transactionId}`)
  }

  updateProcessorTransactionId(transactionId: string, processorTransactionId: string) {
    return this.client.put(`/transaction/admin/updateProcessorTransactionId/${transactionId}`, { processorTransactionId })
  }

  getTransactionStatus(transactionId: string) {
    return this.client.get(`/payment/getTransactionStatusAsString/${transactionId}`)
  }

  getBillingDetails(transactionId: string) {
    return this.client.get(`/transaction/user/billingDetails/${transactionId}`)
  }

  getAllCustomers() {
    return this.client.get('transaction/user/getAllCustomersList')
  }

  getAllCardTypes() {
    return this.client.get('transaction/user/getAllCardTypesList');
  }


  getTransactionsListForSettlement(filters: Partial<TransactionFilters>, pageInfo: ApiPageInfo) {

    const queryString = this.getQueryParams(pageInfo);

    return this.client.post(`transaction/user/transactionsListForSettlement${queryString}`, filters);

  }

  getCommissionsByMidId(midId: string) {
    return this.client.get(`commission/getByMidId/${midId}`)
  }

  //RECONCILIATION

  getReconcileTransactions(filters?: Partial<ReconcileTransactionsFilters>, pageInfo?: ApiPageInfo) {
    const queryString = this.getQueryParams(pageInfo);
    return this.client.post(`/reconciliation/getReconcileTransactions${queryString}`, filters)
  }

  changeReconcileStatus(transactionId: string, reconcileStatus: 'OUTSTANDING' | 'RECONCILED') {
    return this.client.put(`/reconciliation/changeReconcileStatus/${transactionId}/${reconcileStatus}`)
  }

  uploadReconciliationExcel(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.client.post('/reconciliation/uploadReconciliationExcel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  getReconcileLogs(pageInfo?: ApiPageInfo) {
    const queryString = this.getQueryParams(pageInfo);
    return this.client.get(`/reconciliation/getReconcileLogs${queryString}`)
  }




  // GENERATE DOCUMENTS 

  generateSettlementExcel(filters: Partial<TransactionFilters>) {
    return this.client.post(`/documents/generateSettlementReportExcel`, filters, {
      responseType: 'arraybuffer',
    })
  }

  generateExcel(filters: Partial<TransactionFilters>) {
    return this.client.post(`/documents/transactionExcel`, filters, {
      responseType: "arraybuffer",
    })
  }

  generateMidsExcel(merchantId: string) {
    return this.client.post(`/documents/midLimitsExcel/${merchantId}`,{},{
      responseType: "arraybuffer",
    })
  }

  generatePdf(filters: Partial<TransactionFilters>) {
    return this.client.post(`/documents/generateSettlementReport`, filters, {
      responseType: "arraybuffer",
    })
  }

  generateSettlementReportExcelAndPDF(body: any) {
    return this.client.post(`/documents/generateSettlementReportExcel&PDF`, body, {
      responseType: "arraybuffer",
    })
  }

  generateRollingSettlementReportExcelAndPDF(body: any) {
    return this.client.post(`/documents/generateRollingSettlementReportExcel&PDF`, body, {
      responseType: "arraybuffer"
    })
  }

  //INVOICES

  bulkDownloadInvoices(filters: Partial<TransactionFilters>) {
    return this.client.post(`/invoice/user/bulk`, filters, {
      responseType: 'arraybuffer',
    })
  }

  downloadInvoice(transactionId: string) {
    return this.client.get(`/invoice/user/${transactionId}`, {
      responseType: 'arraybuffer',
    })
  }

  //BAN-LIST

  getAllUsersBanList() {
    return this.client.get('/ban-list/getAll')
  }

  addUserToBanList(createUser: CreateBanUser) {
    return this.client.post('/ban-list/create', createUser)
  }

  exportUsersBanList() {
    return this.client.get('/ban-list/export', {
      responseType: 'arraybuffer',
    })
  }

  removeUserFromBanList(id: string) {
    return this.client.delete(`/ban-list/delete/${id}`)
  }

  //STATISTICS

  getSuccessAndErrorStatistics(filters: Partial<TransactionFilters>) {
    return this.client.post(`/kpi/getSuccessAndErrorsStatistics`, filters);
  }


  getTransactionsStatusStatistics(filters: Partial<TransactionFilters>) {
    const queryString = this.getQueryParams(filters);
    return this.client.get(`/kpi/getTransactionsStatusStatistics${queryString}`);
  }

  getProcessorStatistics(filters?: Partial<TransactionFilters>) {
    const queryString = this.getQueryParams(filters);
    return this.client.get(`/kpi/getProcessorStatistics${queryString}`);
  }

  getMerchantStatistics(filters?: Partial<TransactionFilters>) {
    const queryString = this.getQueryParams(filters);
    return this.client.get(`/kpi/getMerchantStatistics${queryString}`);
  }

  getCardTypeStatistics(filters: Partial<TransactionFilters>) {
    const queryString = this.getQueryParams(filters);
    return this.client.get(`/kpi/getCardTypeAndCardOwnerStatistics${queryString}`);
  }
  getMerchantsTransactionsPercents() {
    return this.client.get(`/kpi/getMerchantsTransactionsPercents`);
  }

  getMidStatistics(filters: Partial<TransactionFilters>) {
    const queryString = this.getQueryParams(filters);
    return this.client.get(`/kpi/getMidStatistics${queryString}`)
  }

  getDailyStatistics(filters: Partial<DailyProcessingFilters>) {
    const queryString = this.getQueryParams(filters);
    return this.client.post(`/kpi/dashboard${queryString}`)
  }

  getChartTransactionsData(filters?: Partial<TransactionFilters>) {
    const queryString = this.getQueryParams(filters);
    return this.client.get(`/kpi/getChartTransactionsData${queryString}`);
  }

  getChartProcessorData(filters?: Partial<TransactionFilters>) {
    const queryString = this.getQueryParams(filters);
    return this.client.get(`/kpi/getChartProcessorData${queryString}`);
  }

  getChartMerchantData(filters?: Partial<TransactionFilters>) {
    const queryString = this.getQueryParams(filters);
    return this.client.get(`/kpi/getChartMerchantData${queryString}`);
  }

  getChartCardData(filters?: Partial<TransactionFilters>) {
    const queryString = this.getQueryParams(filters);
    return this.client.get(`/kpi/getChartCardData${queryString}`);
  }

  getChartMidData(filters?: Partial<TransactionFilters>) {
    const queryString = this.getQueryParams(filters);
    return this.client.get(`/kpi/getChartMidData${queryString}`);
  }
  //CYBERSOURCE-INTEGRATION

  paymentFinalStep(merchantId: string, midName: string,) {
    return this.client.post(`cyb/paymentFinalStep/${merchantId}/${midName}`)
  }

  payerSetup(apiKey: string, merchantId: string, midName: string, paymentInformation: PaymentInformation) {
    return this.client.post(`cyb/payerSetup/${merchantId}/${midName}`, paymentInformation)
  }

  payerEnrollment(merchantId: string, midName: string) {
    return this.client.post(`cyb/payerEnrollment/${merchantId}/${midName}`)
  }

  payerAuthenticationValidation(merchantId: string, midName: string) {
    return this.client.post(`cyb/payerEnrollment/${merchantId}/${midName}`)
  }



  private getQueryParams(params: any, addQuestionMark: boolean = true) {
    if (!params || typeof params !== 'object') {
      return '';
    }

    const queryString = Object.keys(params)
      .filter(key => params[key] !== undefined && params[key] !== null && params[key] !== '')
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');

    return queryString && addQuestionMark ? `?${queryString}` : queryString;
  }

}

const apiService = new ApiService();
export { apiService };